import { useEffect, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { URLContext } from '@atlassian/atlassian-studio-routing';

export interface WorkspaceContext {
	workspaces: string[];
	isReady: boolean;
	error?: Error;
}

/**
 * Hook that gathers workspace context based on current url and whether v2 is enabled for the org.
 *
 * @param urlContext
 * @param isFeatureGateInitialized - Whether feature gates are initialized (prevents calling fg() before initialization)
 * @returns WorkspaceContext with workspaces and ready flag to indicate if the app is ready to render
 */
export const useWorkspaceContext = (
	urlContext: URLContext,
	isFeatureGateInitialized: boolean = false,
): WorkspaceContext => {
	const [workspaceContext, setWorkspaceContext] = useState<WorkspaceContext>({
		workspaces: [],
		isReady: false,
		error: undefined,
	});

	const isAppSelectionChangeEnabled =
		isFeatureGateInitialized && fg('asf620_route_arch_and_app_selection_changes');

	useEffect(() => {
		if (!isAppSelectionChangeEnabled) {
			return;
		}

		let newWorkspaceContext: WorkspaceContext;

		if (urlContext.workspaceId) {
			// v2 url with workspace id
			newWorkspaceContext = {
				workspaces: [urlContext.workspaceId],
				isReady: true,
				error: undefined,
			};
		} else if (urlContext.siteId) {
			// v1 url with site id
			// TODO: https://product-fabric.atlassian.net/browse/ASF-857
			// - get org id by site id
			// - get v2 enabled workspace for org
			newWorkspaceContext = {
				workspaces: [],
				isReady: true,
				error: undefined,
			};
		} else {
			// no workspaceId, no siteId
			// TODO: https://product-fabric.atlassian.net/browse/ASF-857
			// - retrieve recent workspace for user
			// - if none -> retrieve all orgs for user
			// - retrieve v2 enabled workspaces for orgs
			newWorkspaceContext = {
				workspaces: [],
				isReady: true,
				error: undefined,
			};
		}

		setWorkspaceContext(newWorkspaceContext);
	}, [urlContext.workspaceId, urlContext.siteId, isAppSelectionChangeEnabled]);

	return workspaceContext;
};
